import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowLeft,
  Users,
  CurrencyDollar,
  ChartLineUp,
  ArrowsClockwise,
  Sparkle,
  User,
  Chat,
  Brain,
  MagnifyingGlass,
  CreditCard,
  Envelope,
  ShieldStar,
  Circle,
  PaperPlaneRight,
  DownloadSimple,
} from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

import api from "@/lib/api";
import { fmtMoney } from "@/lib/defaults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

const chart = {
  grid: "#1f2a44",
  axis: "#94a3b8",
  green: "#10b981",
  blue: "#3b82f6",
  amber: "#f59e0b",
};

const KpiTile = ({ label, value, hint, icon, tone = "default", testId }) => {
  const t =
    tone === "positive"
      ? "text-emerald-300"
      : tone === "negative"
        ? "text-red-300"
        : tone === "money"
          ? "text-emerald-300"
          : "text-white";
  return (
    <div
      data-testid={testId}
      className="glass rounded-2xl p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium">
            {label}
          </p>
          <p className={`tabular text-2xl md:text-3xl font-semibold mt-2 leading-none ${t}`}>
            {value}
          </p>
          {hint && <p className="mt-2 text-[11px] text-slate-500 tabular">{hint}</p>}
        </div>
        <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/25 grid place-items-center text-emerald-300 shrink-0">
          {icon}
        </div>
      </div>
    </div>
  );
};

const useOwnerGate = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const nav = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      nav("/login", { state: { from: "/owner" }, replace: true });
      return;
    }
    (async () => {
      try {
        await api.get("/admin/whoami");
        setAllowed(true);
      } catch {
        setAllowed(false);
      } finally {
        setChecking(false);
      }
    })();
  }, [isAuthenticated, loading, nav]);

  return { allowed, checking, user };
};

const AccessDenied = ({ email }) => (
  <div className="min-h-screen bg-[#0a192f] text-white grid place-items-center px-6">
    <div className="max-w-md w-full glass rounded-3xl p-8 text-center">
      <div className="w-14 h-14 rounded-2xl mx-auto bg-red-500/12 border border-red-500/30 grid place-items-center">
        <ShieldStar size={26} weight="duotone" className="text-red-400" />
      </div>
      <h1 className="mt-5 text-2xl font-semibold tracking-tight">Owner access only</h1>
      <p className="mt-2 text-slate-400 text-sm">
        You're signed in as <span className="text-white">{email}</span>, but this
        account is not the app owner.
      </p>
      <Link
        to="/app"
        className="mt-6 inline-flex items-center gap-2 rounded-lg border border-white/10 hover:border-white/25 px-4 py-2.5 text-sm text-white"
      >
        <ArrowLeft size={14} weight="bold" className="flip-x" />
        Back to app
      </Link>
    </div>
  </div>
);

const OverviewTab = ({ data }) => {
  if (!data) return null;
  const { users, subscribers, guests, revenue, loads, feedback, ai, conversion } = data;
  return (
    <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
      <KpiTile
        testId="kpi-mrr"
        label="MRR"
        value={fmtMoney(subscribers.mrr_usd)}
        hint={`ARR ${fmtMoney(subscribers.arr_usd)}`}
        icon={<ChartLineUp size={16} weight="duotone" />}
        tone="money"
      />
      <KpiTile
        testId="kpi-active-subs"
        label="Active subscribers"
        value={subscribers.active}
        hint={`Premium ${subscribers.by_plan.Premium} · Agency ${subscribers.by_plan.Agency}`}
        icon={<Sparkle size={16} weight="fill" />}
        tone="positive"
      />
      <KpiTile
        testId="kpi-users"
        label="Registered users"
        value={users.total}
        hint={`+${users.new_7d} in last 7d · +${users.new_30d} 30d`}
        icon={<Users size={16} weight="duotone" />}
      />
      <KpiTile
        testId="kpi-guests"
        label="Guests (7d)"
        value={guests.unique_7d}
        hint={`${guests.sessions_7d} sessions`}
        icon={<User size={16} weight="duotone" />}
      />
      <KpiTile
        testId="kpi-revenue"
        label="Revenue"
        value={fmtMoney(revenue.total_usd)}
        hint={`7d ${fmtMoney(revenue.last_7d_usd)} · 30d ${fmtMoney(revenue.last_30d_usd)}`}
        icon={<CurrencyDollar size={16} weight="duotone" />}
        tone="money"
      />
      <KpiTile
        testId="kpi-renewals"
        label="Renewals next 30d"
        value={subscribers.renewals_next_30d}
        hint="Subscriptions ending soon"
        icon={<ArrowsClockwise size={16} weight="bold" />}
      />
      <KpiTile
        testId="kpi-loads"
        label="Loads screened"
        value={loads.total}
        hint={`${loads.last_7d} in last 7d`}
        icon={<ChartLineUp size={16} weight="duotone" />}
      />
      <KpiTile
        testId="kpi-ai"
        label="AI Advisor calls"
        value={ai.calls_total}
        hint={`${ai.calls_7d} in last 7d`}
        icon={<Brain size={16} weight="duotone" />}
      />
      <KpiTile
        testId="kpi-conv-user"
        label="Guest → user"
        value={`${conversion.guest_to_user_pct}%`}
        icon={<ChartLineUp size={16} weight="duotone" />}
      />
      <KpiTile
        testId="kpi-conv-paid"
        label="User → paid"
        value={`${conversion.user_to_paid_pct}%`}
        icon={<CurrencyDollar size={16} weight="duotone" />}
      />
      <KpiTile
        testId="kpi-ai-addon"
        label="AI add-on users"
        value={subscribers.ai_addon}
        icon={<Brain size={16} weight="duotone" />}
      />
      <KpiTile
        testId="kpi-feedback"
        label="Feedback"
        value={feedback.total}
        hint={`${feedback.unread} unread`}
        icon={<Chat size={16} weight="duotone" />}
      />
    </div>
  );
};

const ChartsRow = ({ series }) => (
  <div className="grid lg:grid-cols-2 gap-4 mt-4">
    <div className="glass rounded-2xl p-5">
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium">
        Signups vs Loads
      </p>
      <h3 className="text-white text-lg font-semibold mt-1">Daily activity</h3>
      <div className="h-64 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fill: chart.axis, fontSize: 11 }} stroke={chart.grid} />
            <YAxis tick={{ fill: chart.axis, fontSize: 11 }} stroke={chart.grid} />
            <Tooltip
              contentStyle={{
                background: "#0e2140",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                color: "#e2e8f0",
              }}
            />
            <Legend wrapperStyle={{ color: "#cbd5e1", fontSize: 11 }} />
            <Line type="monotone" name="Signups" dataKey="signups" stroke={chart.green} strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" name="Loads" dataKey="loads" stroke={chart.blue} strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
    <div className="glass rounded-2xl p-5">
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium">
        Revenue per day
      </p>
      <h3 className="text-white text-lg font-semibold mt-1">Daily revenue</h3>
      <div className="h-64 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fill: chart.axis, fontSize: 11 }} stroke={chart.grid} />
            <YAxis tick={{ fill: chart.axis, fontSize: 11 }} stroke={chart.grid} />
            <Tooltip
              contentStyle={{
                background: "#0e2140",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                color: "#e2e8f0",
              }}
              formatter={(v) => [`$${Number(v).toFixed(2)}`, "Revenue"]}
            />
            <Bar dataKey="revenue" fill={chart.green} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
);

const UsersTable = () => {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users", {
        params: { limit: 100, skip: 0, search: search || undefined, plan: plan || undefined },
      });
      setRows(res.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [search, plan]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="p-4 md:p-5 border-b border-white/8 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} weight="bold" className="absolute inset-y-0 start-3 my-auto text-slate-500" />
          <Input
            data-testid="users-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="h-9 rounded-lg bg-white/[0.04] border-white/10 text-white ps-9 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          {[
            { v: "", l: "All" },
            { v: "free", l: "Free" },
            { v: "Premium", l: "Premium" },
            { v: "Agency", l: "Agency" },
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`users-filter-${f.v || "all"}`}
              onClick={() => setPlan(f.v)}
              className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.14em] font-medium border ${
                plan === f.v
                  ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                  : "border-white/10 text-slate-300 hover:border-white/25"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading && !rows ? (
          <div className="p-6 text-slate-400 text-sm">Loading…</div>
        ) : rows?.items?.length ? (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
                <th className="text-start p-4">User</th>
                <th className="text-start p-4">Method</th>
                <th className="text-start p-4">Plan</th>
                <th className="p-4">AI</th>
                <th className="text-start p-4">Joined</th>
                <th className="text-start p-4">Renews</th>
              </tr>
            </thead>
            <tbody>
              {rows.items.map((u) => (
                <tr key={u.user_id} className="border-t border-white/6">
                  <td className="p-4">
                    <p className="text-white font-medium">{u.name}</p>
                    <p className="text-slate-500 text-xs">{u.email}</p>
                  </td>
                  <td className="p-4 text-slate-300 text-xs uppercase tracking-wider">
                    {u.auth_method}
                  </td>
                  <td className="p-4">
                    {u.is_subscribed ? (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15 rounded-md text-[10px] uppercase tracking-[0.12em]">
                        {u.plan_type || "Premium"}
                      </Badge>
                    ) : (
                      <Badge className="bg-white/8 text-slate-300 border border-white/10 hover:bg-white/8 rounded-md text-[10px] uppercase tracking-[0.12em]">
                        Free
                      </Badge>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    {u.has_ai_advisor ? (
                      <Sparkle size={14} weight="fill" className="text-emerald-400 inline" />
                    ) : (
                      <span className="text-slate-600">·</span>
                    )}
                  </td>
                  <td className="p-4 text-slate-400 text-xs tabular">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  <td className="p-4 text-slate-400 text-xs tabular">
                    {u.subscription_current_period_end
                      ? new Date(u.subscription_current_period_end).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-slate-400 text-sm">No users match.</div>
        )}
      </div>
      {rows && (
        <div className="p-3 text-[11px] text-slate-500 border-t border-white/8 text-end tabular">
          Showing {rows.count} of {rows.total}
        </div>
      )}
    </div>
  );
};

const PaymentsTable = () => {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/payments", {
        params: { limit: 100, status: status || undefined },
      });
      setRows(res.data);
    } catch {
      toast.error("Failed to load payments");
    }
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="p-4 md:p-5 border-b border-white/8 flex items-center gap-2 flex-wrap">
        {[
          { v: "", l: "All" },
          { v: "paid", l: "Paid" },
          { v: "initiated", l: "Initiated" },
          { v: "expired", l: "Expired" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`payments-filter-${f.v || "all"}`}
            onClick={() => setStatus(f.v)}
            className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.14em] font-medium border ${
              status === f.v
                ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                : "border-white/10 text-slate-300 hover:border-white/25"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        {rows?.items?.length ? (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
                <th className="text-start p-4">Session</th>
                <th className="text-start p-4">Email</th>
                <th className="text-start p-4">Plan</th>
                <th className="text-end p-4">Amount</th>
                <th className="text-start p-4">Status</th>
                <th className="text-start p-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.items.map((t) => (
                <tr key={t.session_id} className="border-t border-white/6">
                  <td className="p-4 font-mono text-[10px] text-slate-400 max-w-[140px] truncate">
                    {t.session_id}
                  </td>
                  <td className="p-4 text-slate-300 text-xs">{t.email || "—"}</td>
                  <td className="p-4 text-slate-300 text-xs">{t.plan_type || t.plan_id}</td>
                  <td className="p-4 text-end tabular font-semibold text-white">
                    ${Number(t.amount || 0).toFixed(2)}
                  </td>
                  <td className="p-4">
                    <Badge
                      className={`rounded-md text-[10px] uppercase tracking-[0.12em] border ${
                        t.payment_status === "paid"
                          ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/15"
                          : "bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/15"
                      }`}
                    >
                      {t.payment_status || t.status || "—"}
                    </Badge>
                  </td>
                  <td className="p-4 text-slate-400 text-xs tabular">
                    {t.created_at
                      ? new Date(t.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-slate-400 text-sm">
            No payments yet. Once someone subscribes they'll appear here.
          </div>
        )}
      </div>
    </div>
  );
};

const FeedbackTable = () => {
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState("");
  const [unread, setUnread] = useState(false);
  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/feedback", {
        params: { limit: 100, kind: kind || undefined, unread_only: unread || undefined },
      });
      setRows(res.data);
    } catch {
      toast.error("Failed to load feedback");
    }
  }, [kind, unread]);
  useEffect(() => {
    load();
  }, [load]);

  const toggleRead = async (id) => {
    try {
      await api.post(`/admin/feedback/${id}/toggle-read`);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="p-4 md:p-5 border-b border-white/8 flex items-center gap-2 flex-wrap">
        {[
          { v: "", l: "All" },
          { v: "bug", l: "Bug" },
          { v: "idea", l: "Idea" },
          { v: "praise", l: "Praise" },
          { v: "other", l: "Other" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`feedback-filter-${f.v || "all"}`}
            onClick={() => setKind(f.v)}
            className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.14em] font-medium border ${
              kind === f.v
                ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                : "border-white/10 text-slate-300 hover:border-white/25"
            }`}
          >
            {f.l}
          </button>
        ))}
        <button
          data-testid="feedback-filter-unread"
          onClick={() => setUnread((v) => !v)}
          className={`ms-auto px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.14em] font-medium border ${
            unread
              ? "bg-red-500/15 border-red-500/30 text-red-300"
              : "border-white/10 text-slate-300 hover:border-white/25"
          }`}
        >
          Unread only
        </button>
      </div>
      <div className="divide-y divide-white/6">
        {rows?.items?.length ? (
          rows.items.map((f) => (
            <div key={f.id} className="p-4 md:p-5 hover:bg-white/[0.02]">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    className={`rounded-md text-[10px] uppercase tracking-[0.12em] border ${
                      f.kind === "bug"
                        ? "bg-red-500/15 text-red-300 border-red-500/30 hover:bg-red-500/15"
                        : f.kind === "idea"
                          ? "bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/15"
                          : f.kind === "praise"
                            ? "bg-pink-500/15 text-pink-300 border-pink-500/30 hover:bg-pink-500/15"
                            : "bg-white/10 text-slate-300 border-white/15 hover:bg-white/10"
                    }`}
                  >
                    {f.kind}
                  </Badge>
                  {f.rating && (
                    <span className="text-amber-300 text-xs tabular">
                      ★ {f.rating}/5
                    </span>
                  )}
                  {f.page && (
                    <span className="text-slate-500 text-xs">{f.page}</span>
                  )}
                  {!f.read && (
                    <span className="flex items-center gap-1 text-emerald-300 text-[10px] uppercase tracking-[0.14em]">
                      <Circle size={6} weight="fill" />
                      New
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 tabular shrink-0">
                  {f.created_at &&
                    new Date(f.created_at).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  <button
                    data-testid={`feedback-toggle-${f.id}`}
                    onClick={() => toggleRead(f.id)}
                    className="text-slate-400 hover:text-emerald-300"
                    title="Mark as read/unread"
                  >
                    {f.read ? "↺ Unread" : "✓ Read"}
                  </button>
                </div>
              </div>
              <p className="mt-2 text-slate-100 text-sm leading-relaxed">
                {f.message}
              </p>
              {f.email && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
                  <Envelope size={12} weight="duotone" />
                  {f.email}
                </p>
              )}
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-slate-400 text-sm">No feedback yet.</div>
        )}
      </div>
    </div>
  );
};

const DigestTab = () => {
  const [status, setStatus] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [statusRes, htmlRes] = await Promise.all([
        api.get("/admin/digest/status"),
        api.get("/admin/digest/preview", { responseType: "text" }),
      ]);
      setStatus(statusRes.data);
      setPreviewHtml(htmlRes.data);
    } catch {
      toast.error("Failed to load digest");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sendNow = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await api.post("/admin/digest/send-now");
      setSendResult(res.data);
      if (res.data.sent > 0) toast.success(`Digest sent to ${res.data.sent} owner(s)`);
      else if (res.data.skipped) toast.info("Resend key not configured yet");
      load();
    } catch {
      toast.error("Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  if (!status) {
    return (
      <div className="glass rounded-2xl p-6 text-slate-400 text-sm">Loading digest…</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="glass rounded-2xl p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-medium">
              Daily Email Digest
            </p>
            <h3 className="mt-1 text-xl text-white font-semibold tracking-tight">
              Sends automatically at {String(status.digest_hour_utc).padStart(2, "0")}:00 UTC
            </h3>
            <p className="mt-2 text-slate-400 text-sm">
              A polished snapshot of the last 24 hours — MRR, new users, revenue,
              guest sessions, unread feedback, and recent payments — delivered to every
              email in <code className="text-emerald-300 text-xs">OWNER_EMAILS</code>.
            </p>
          </div>
          <div className="flex flex-col gap-2 min-w-[220px]">
            <Badge
              className={`w-fit rounded-md text-[10px] uppercase tracking-[0.14em] border ${
                status.resend_configured
                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/15"
                  : "bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/15"
              }`}
            >
              {status.resend_configured ? "Resend configured" : "Resend key missing"}
            </Badge>
            <div className="text-[11px] text-slate-500 tabular">
              Recipients: {status.owner_emails.join(", ")}
            </div>
            {status.last_run?.sent_at && (
              <div className="text-[11px] text-slate-500 tabular">
                Last run: {new Date(status.last_run.sent_at).toLocaleString("en-US")}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            data-testid="btn-send-digest-now"
            onClick={sendNow}
            disabled={sending}
            className="btn-neon rounded-lg h-10 px-4 text-sm"
          >
            {sending ? (
              <>
                <ArrowsClockwise size={14} weight="bold" className="animate-spin me-1.5" />
                Sending…
              </>
            ) : (
              <>
                <PaperPlaneRight size={14} weight="bold" className="flip-x me-1.5" />
                Send digest now
              </>
            )}
          </Button>
          <a
            href="https://resend.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg h-10 px-4 border border-white/15 text-slate-200 hover:text-white text-sm hover:bg-white/[0.05]"
          >
            Resend dashboard
          </a>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-lg h-10 px-4 border-white/15 text-slate-200 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] text-sm"
          >
            <ArrowsClockwise size={14} weight="bold" className="me-1.5" />
            Refresh
          </Button>
        </div>

        {!status.resend_configured && (
          <div className="mt-4 rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-xs text-amber-200">
            <p className="font-medium">To start receiving emails:</p>
            <ol className="mt-1.5 list-decimal ps-4 space-y-1">
              <li>Sign up at <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline text-emerald-300">resend.com</a> (free, 3,000 emails/month)</li>
              <li>Copy your API key from the Resend dashboard</li>
              <li>Add <code className="text-emerald-300">RESEND_API_KEY="re_XXXX"</code> to <code className="text-emerald-300">/app/backend/.env</code></li>
              <li>Restart backend — done.</li>
            </ol>
          </div>
        )}

        {sendResult && (
          <div
            data-testid="digest-send-result"
            className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs text-slate-300"
          >
            {sendResult.skipped ? (
              <div className="text-amber-300">{sendResult.reason}</div>
            ) : (
              <div>
                Sent to <span className="text-emerald-300 font-semibold">{sendResult.sent}</span> recipient(s).
                {sendResult.subject && (
                  <div className="text-slate-500 mt-1">Subject: {sendResult.subject}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-3 md:p-4">
        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium mb-3 px-2">
          Live preview (exactly what recipients see)
        </p>
        <div className="rounded-xl overflow-hidden border border-white/8 bg-slate-50">
          <iframe
            data-testid="digest-preview-iframe"
            srcDoc={previewHtml}
            title="Digest preview"
            sandbox=""
            className="w-full"
            style={{ height: 700, background: "white", border: 0 }}
          />
        </div>
      </div>
    </div>
  );
};



export default function Owner() {
  const { allowed, checking, user } = useOwnerGate();
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(30);

  const loadOverview = useCallback(async () => {
    try {
      const [ov, ts] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/timeseries", { params: { days } }),
      ]);
      setOverview(ov.data);
      setSeries(ts.data.daily || []);
    } catch (e) {
      /* handled via gate */
    }
  }, [days]);

  useEffect(() => {
    if (allowed) loadOverview();
  }, [allowed, loadOverview]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a192f] text-white grid place-items-center">
        <div className="w-8 h-8 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (allowed === false) {
    return <AccessDenied email={user?.email} />;
  }

  return (
    <div className="min-h-screen bg-[#0a192f] text-white landing-root">
      <div className="ticker-line" />

      <header className="max-w-[1400px] mx-auto px-5 md:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-400/30 grid place-items-center">
            <ShieldStar size={18} weight="duotone" className="text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-medium leading-none">
              Owner Console
            </p>
            <h1 className="text-lg font-semibold tracking-tight leading-tight mt-1">
              Business overview
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`btn-owner-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.14em] font-medium border ${
                days === d
                  ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                  : "border-white/10 text-slate-300 hover:border-white/25"
              }`}
            >
              {d}d
            </button>
          ))}
          <Button
            data-testid="btn-owner-refresh"
            onClick={loadOverview}
            variant="outline"
            className="h-8 rounded-lg border-white/15 text-slate-200 hover:text-white text-xs bg-white/[0.04] hover:bg-white/[0.08]"
          >
            <ArrowsClockwise size={12} weight="bold" className="me-1" />
            Refresh
          </Button>
          <Link
            to="/app"
            className="ms-2 flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-3 py-2 rounded-lg border border-white/10 hover:border-white/25"
          >
            <ArrowLeft size={12} weight="bold" className="flip-x" />
            App
          </Link>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-8 pb-16 space-y-6">
        <OverviewTab data={overview} />
        <ChartsRow series={series} />

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="bg-white/[0.04] border border-white/10">
            <TabsTrigger
              value="users"
              data-testid="tab-users"
              className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-slate-300"
            >
              <Users size={14} weight="bold" className="me-1.5" />
              Users
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              data-testid="tab-payments"
              className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-slate-300"
            >
              <CreditCard size={14} weight="bold" className="me-1.5" />
              Payments
            </TabsTrigger>
            <TabsTrigger
              value="feedback"
              data-testid="tab-feedback"
              className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-slate-300"
            >
              <Chat size={14} weight="bold" className="me-1.5" />
              Feedback
              {overview?.feedback?.unread > 0 && (
                <Badge className="ms-2 bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/15 rounded-md text-[10px]">
                  {overview.feedback.unread}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="digest"
              data-testid="tab-digest"
              className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-slate-300"
            >
              <Envelope size={14} weight="bold" className="me-1.5" />
              Digest
            </TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-4">
            <UsersTable />
          </TabsContent>
          <TabsContent value="payments" className="mt-4">
            <PaymentsTable />
          </TabsContent>
          <TabsContent value="feedback" className="mt-4">
            <FeedbackTable />
          </TabsContent>
          <TabsContent value="digest" className="mt-4">
            <DigestTab />
          </TabsContent>
        </Tabs>
      </main>

      <Toaster position="top-right" richColors closeButton theme="dark" />
    </div>
  );
}
